import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import axios from 'axios';

const STATUS_URL = '/api/DatabaseBackup/restore/status';

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export default function RestoreGuard() {
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const wasRunning = useRef(false);
  const timer = useRef(null);

  const poll = async () => {
    try {
      const { data } = await axios.get(STATUS_URL);
      setStatus(data);
      if (data.running) {
        wasRunning.current = true;
      } else if (wasRunning.current) {
        wasRunning.current = false;
        setResult(data);
      }
    } catch (e) {
      // átmeneti hiba: a következő lekérdezésnél újrapróbáljuk
    }
  };

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, status?.running ? 3000 : 10000);
    const onStart = () => poll();
    window.addEventListener('restore-started', onStart);
    return () => {
      clearInterval(timer.current);
      window.removeEventListener('restore-started', onStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.running]);

  return (
    <>
      {status?.running && (
        <Box
          sx={{
            position: 'fixed', inset: 0, zIndex: 20000,
            bgcolor: 'rgba(15, 23, 41, 0.92)', color: '#fff',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', p: 3
          }}
        >
          <CircularProgress size={72} sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 1 }}>Adatbázis-visszaállítás folyamatban…</Typography>
          <Typography variant="h3" sx={{ fontFamily: 'monospace', mb: 2 }}>{fmt(status.elapsedSeconds || 0)}</Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>☕ Ez eltarthat egy ideig — igyál egy kávét!</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, maxWidth: 520 }}>
            Amíg a visszaállítás tart, az alkalmazás nem használható. Ne zárd be a böngészőt és ne indítsd újra a szervert.
            Ha az oldalt frissíted, ez a képernyő visszajön, amíg a folyamat be nem fejeződik.
          </Typography>
        </Box>
      )}

      <Dialog open={!!result} maxWidth="sm" fullWidth>
        <DialogTitle>Visszaállítás befejeződött</DialogTitle>
        <DialogContent>
          {result?.phase === 'failed' ? (
            <Alert severity="error">A visszaállítás nem sikerült: {result.failure || 'ismeretlen hiba'}</Alert>
          ) : result?.phase === 'done_with_errors' ? (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>
                A visszaállítás lefutott, de {result.errorCount} SQL hibaüzenet keletkezett (pl. már létező elemek).
              </Alert>
              {result.errors?.length > 0 && (
                <Box component="pre" sx={{ maxHeight: 220, overflow: 'auto', fontSize: 12, bgcolor: '#f5f5f5', p: 1 }}>
                  {result.errors.join('\n')}
                </Box>
              )}
            </>
          ) : (
            <Alert severity="success">Az adatok sikeresen visszaállítva. ({fmt(result?.elapsedSeconds || 0)})</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => window.location.reload()}>Rendben</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
